import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { lazy, Suspense } from "react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import { CmdKProvider } from "./contexts/CmdKContext";
import { DocRefProvider } from "./contexts/DocRefContext";
import { Layout } from "./components/Layout";
import { ErrorBoundary } from "./components/ErrorBoundary";

// ─────────────────────────── Page imports (eager) ───────────────────────────
import { Login } from "./pages/Login";
import { Dashboard } from "./pages/dashboard";
import { OrdenesTrabajoList } from "./pages/OrdenesTrabajoList";
import { OrdenTrabajoForm } from "./pages/OrdenTrabajoForm";
import { OrdenTrabajoDetail } from "./pages/OrdenTrabajoDetail";
import { ProyectosList } from "./pages/ProyectosList";
import { ProyectoForm } from "./pages/ProyectoForm";
import { ClientesList } from "./pages/ClientesList";
import { Configuracion } from "./pages/Configuracion";
import TarjetasPagoPage from "./pages/configuracion/TarjetasPagoPage";
import MediosPagoPage from "./pages/configuracion/MediosPagoPage";
import CondicionesPagoPage from "./pages/configuracion/CondicionesPagoPage";
import { AccessDatabase } from "./pages/AccessDatabase";
import { Reportes } from "./pages/Reportes";
import {
  CostoProyectoPage,
  CostoOTPage,
  MantencionFacturacionPage,
} from "./pages/PlaceholderPages";
import RRHHDashboard from "./pages/rrhh/RRHHDashboard";
import TrabajadoresPage from "./pages/rrhh/TrabajadoresPage";
import VacacionesPage from "./pages/rrhh/VacacionesPage";
import ControlHHUnificado from "./pages/rrhh/ControlHHUnificado";
import ParteDiarioTerreno from "./pages/rrhh/ParteDiarioTerreno";
import MonitorPartesPage from "./pages/rrhh/MonitorPartesPage";
import MisPartes from "./pages/rrhh/MisPartes";
import MisVBPage from "./pages/MisVB";
import BandejaAprobaciones from "./pages/BandejaAprobaciones";
import ResumenAlmuerzos from "./pages/rrhh/ResumenAlmuerzos";
import ResumenCasino from "./pages/rrhh/ResumenCasino";
import TarjetaControlPage from "./pages/rrhh/TarjetaControlPage";
import ControlErroresPage from "./pages/rrhh/ControlErroresPage";
import AsistenciaMensualPage from "./pages/rrhh/AsistenciaMensualPage";
import PanelPersonalPage from "./pages/rrhh/PanelPersonalPage";
import ProcedimientosRRHH from "./pages/rrhh/ProcedimientosRRHH";
import HuerfanosPartesPage from "./pages/rrhh/HuerfanosPartesPage";
import { ActivoCirculantePage } from "./pages/ActivoCirculante";
import { ComprasResumen } from "./pages/compras/ComprasResumen";
import { OrdenesCompraList } from "./pages/compras/OrdenesCompraList";
import { CostoMaterialesOT } from "./pages/compras/CostoMaterialesOT";
import { ReportePendientes } from "./pages/compras/ReportePendientes";
import { ResumenComprasOTs } from "./pages/compras/ResumenComprasOTs";
import { EstadoComprasOT } from "./pages/compras/EstadoComprasOT";
import { FirmaVBLote } from "./pages/compras/FirmaVBLote";
import ProveedoresList from "./pages/compras/ProveedoresList";
import AuditoriaNombresProveedores from "./pages/compras/AuditoriaNombresProveedores";
import ItemsOC from "./pages/compras/ItemsOC";
import ManualUso from "./pages/ManualUso";
import CalidadDashboard from "./pages/CalidadDashboard";
import CodeHealthPage from "./pages/CodeHealthPage";
import TarjetasReportePage from "./pages/admin/TarjetasReportePage";
import TarjetasConciliacionPage from "./pages/admin/TarjetasConciliacionPage";
import CajasRendirPage from "./pages/admin/CajasRendirPage";
import CajasRendirReportePage from "./pages/admin/CajasRendirReportePage";
import { AlegraIntegration } from "./pages/AlegraIntegration";
import FlujoCajaPage from "./pages/FlujoCajaPage";
import RegularizacionAnticiposPage from "./pages/contabilidad/RegularizacionAnticiposPage"; // Iter 387
import CxPNominasPage from "./pages/CxPNominasPage";
import ChequesPorPagarPage from "./pages/flujo_caja/ChequesPorPagarPage";
import ChequesEmitidosPage from "./pages/flujo_caja/ChequesEmitidosPage";
// Iter 369 — AnticiposProveedorPage (flujo_caja) retirada del router: duplicaba /contabilidad/anticipos
import TefConfigPage from "./pages/TefConfigPage";
import { CuentasPorCobrar } from "./pages/CuentasPorCobrar";
import { CuentasPorPagar } from "./pages/CuentasPorPagar";
import { GerenciaReport } from "./pages/reports/GerenciaReport";
import RCVComparator from "./pages/RCVComparator";
import ContactosPage from "./pages/ContactosPage";
import DirectorioPage from "./pages/DirectorioPage";
import InformeProyecto from "./pages/reportes/InformeProyecto";
import InformeCostoOT from "./pages/reportes/InformeCostoOT";
import ProformasPendientes from "./pages/ProformasPendientes";
import RecepcionesPendientes from "./pages/RecepcionesPendientes";
import ContabilizarOC from "./pages/ContabilizarOC";
import SeguimientoFacturas from "./pages/SeguimientoFacturas";
import CuentasPorPagarRCV from "./pages/CuentasPorPagarRCV";
import FacturaVenta from "./pages/FacturaVenta";
import RecepcionOC from "./pages/RecepcionOC";
import RecepcionOCPage from "./pages/bodega/RecepcionOCPage";
import RecepcionesListPage from "./pages/bodega/RecepcionesListPage";
import FacturasPendientesRecepcion from "./pages/finanzas/FacturasPendientesRecepcion";
import PagosBloqueadosPage from "./pages/finanzas/PagosBloqueadosPage";
import LotesServiciosPage from "./pages/finanzas/LotesServiciosPage";
import StockBodega from "./pages/StockBodega";
import MaestroMateriales from "./pages/MaestroMateriales";
import StockInsumosPage from "./pages/bodega/StockInsumosPage";
import DespachosPage from "./pages/bodega/DespachosPage";
import HerramientasCargoPage from "./pages/bodega/HerramientasCargoPage";
import PreciosHistoricosPage from "./pages/bodega/PreciosHistoricosPage";
import MaestroHerramientasPage from "./pages/bodega/MaestroHerramientasPage";
import PanolesPage from "./pages/bodega/PanolesPage";
import PanolDetallePage from "./pages/bodega/PanolDetallePage";
import DistribucionHerramientasPage from "./pages/bodega/DistribucionHerramientasPage";
import InventarioFisicoPage from "./pages/bodega/InventarioFisicoPage";
import DashboardBodega from "./pages/bodega/DashboardBodega";
import QRImprimirPage from "./pages/bodega/QRImprimirPage";
import InventarioSemanalPage from "./pages/bodega/InventarioSemanalPage";
import TransferenciasPage from "./pages/bodega/TransferenciasPage";
import TransferenciaRecibirPage from "./pages/bodega/TransferenciaRecibirPage";
import HerramientaTimelinePage from "./pages/bodega/HerramientaTimelinePage";
import PanolesBulkResponsablePage from "./pages/bodega/PanolesBulkResponsablePage";
import OCPendientesRecepcionPage from "./pages/bodega/OCPendientesRecepcionPage";
import FacturasContabilizar from "./pages/FacturasContabilizar";
import GestionUsuarios from "./pages/GestionUsuarios";
import AuditLogPage from "./pages/AuditLogPage";
import FeriadosHorarioPage from "./pages/FeriadosHorarioPage";
import GenerarOCPage from "./pages/compras/GenerarOCPage";
import AuditoriaTrazabilidad from "./pages/compras/AuditoriaTrazabilidad";
import Requisiciones from "./pages/compras/Requisiciones";
import BuscadorMateriales from "./pages/compras/BuscadorMateriales";
import SeguimientoOCPage from "./pages/compras/SeguimientoOC";
import TrazabilidadOT from "./pages/compras/TrazabilidadOT";
import SolicitudCotizacion from "./pages/compras/SolicitudCotizacion";
import { CotizacionesList } from "./pages/compras/CotizacionesList";
import ComparativoCotizacion from "./pages/compras/ComparativoCotizacion";
import { RendicionesGastosList } from "./pages/compras/RendicionesGastosList";
import { RendicionGastosDetail } from "./pages/compras/RendicionGastosDetail";
import CotizarPublico from "./pages/public/CotizarPublico";
import RegistroDTE from "./pages/RegistroDTE";
import RCVImportPage from "./pages/contabilidad/RCVImportPage";
import RCVReportesPage from "./pages/contabilidad/RCVReportesPage";
import EgresosPage from "./pages/contabilidad/EgresosPage";
import IngresosPage from "./pages/contabilidad/IngresosPage";
import CalendarioBancarioPage from "./pages/configuracion/CalendarioBancarioPage";
import SegurosPage from "./pages/contabilidad/SegurosPage";
import SolicitarEstadosCuenta from "./pages/contabilidad/SolicitarEstadosCuenta";
import DuplicadosFacturasPage from "./pages/contabilidad/DuplicadosFacturasPage";
import NotasCreditoPage from "./pages/contabilidad/NotasCreditoPage";
import RelinkearProformaPage from "./pages/contabilidad/RelinkearProformaPage";
import MonitoreoSistema from "./pages/Config/MonitoreoSistema";
import Notificaciones from "./pages/Config/Notificaciones";
import SyncAccessPanel from "./pages/Config/SyncAccessPanel";
import RecordatoriosPage from "./pages/Config/RecordatoriosPage";
import RecordatoriosFacRecepPage from "./pages/Config/RecordatoriosFacRecepPage";
import ReportesProgramadosPage from "./pages/Config/ReportesProgramadosPage";
import ReportesAnalyticsPage from "./pages/Config/ReportesAnalyticsPage";
import RecordatoriosInventarioPage from "./pages/Config/RecordatoriosInventarioPage";
import MigracionAccessHtasPage from "./pages/Config/MigracionAccessHtasPage";
import HealthCheckPage from "./pages/Config/HealthCheckPage";
import CuentasAlegraPage from "./pages/Config/CuentasAlegraPage";
import ApiIAPage from "./pages/Config/ApiIAPage";
import OAuthAutorizar from "./pages/OAuthAutorizar";
import ConciliacionBancaria from "./pages/finanzas/ConciliacionBancaria";
import ConciliacionProveedoresPage from "./pages/conciliacion/ConciliacionProveedoresPage";
import Procedimientos from "./pages/Procedimientos";
import CotizadorHistorico from "./pages/informacion/CotizadorHistorico";
import EmisionDTEList from "./pages/emision/EmisionDTEList";
import EmisionDTEDetail from "./pages/emision/EmisionDTEDetail";

// Code-splitting (lazy) para AceptaRechazoDTE: 1885 líneas, ~80kB gzipped.
// Reduce el bundle inicial; sólo se carga al entrar a /contabilidad/acepta-rechazo-dte.
const AceptaRechazoDTE = lazy(() => import("./pages/AceptaRechazoDTE"));

const dteFallback = <div className="p-8 text-sm text-muted-foreground">Cargando módulo DTE…</div>;
const LazyAceptaRechazoDTE = (
  <Suspense fallback={dteFallback}><AceptaRechazoDTE /></Suspense>
);

// ─────────────────────────── Auth wrappers ───────────────────────────

const TERRENO_ALLOWED = [
  '/rrhh/parte-diario',
  '/rrhh/monitor-partes',
  '/rrhh/resumen-almuerzos',
  // Carlos pidió que TODOS (incluido terreno) puedan crear requisiciones.
  '/compras/requisiciones',
];

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (user) {
    if (user.rol === 'terreno') return <Navigate to="/rrhh/parte-diario" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

const TerrenoRedirect = () => {
  const { user } = useAuth();
  if (user?.rol === 'terreno') return <Navigate to="/rrhh/parte-diario" replace />;
  return <Navigate to="/dashboard" replace />;
};

const TerrenoGuard = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  if (user?.rol === 'terreno' && !TERRENO_ALLOWED.some(p => location.pathname.startsWith(p))) {
    return <Navigate to="/rrhh/parte-diario" replace />;
  }
  return children;
};

/** Helper: route protegida con PrivateRoute. Reduce 5 líneas a 1 por ruta. */
const P = ({ children }) => <PrivateRoute>{children}</PrivateRoute>;

/** Helper: route con PrivateRoute + TerrenoGuard (ej. RRHH backoffice). */
const PT = ({ children }) => <PrivateRoute><TerrenoGuard>{children}</TerrenoGuard></PrivateRoute>;

// ─────────────────────────── Route maps por dominio ───────────────────────────
// Cada map [path, element]. Reduce 4 líneas de boilerplate por ruta a 1.

const informacionRoutes = [
  ["/ordenes-trabajo",            <OrdenesTrabajoList />],
  ["/ordenes-trabajo/nueva",      <OrdenTrabajoForm />],
  ["/ordenes-trabajo/:id",        <OrdenTrabajoDetail />],
  ["/ordenes-trabajo/:id/editar", <OrdenTrabajoForm />],
  ["/proyectos",                  <ProyectosList />],
  ["/proyectos/nuevo",            <ProyectoForm />],
  ["/proyectos/:id/editar",       <ProyectoForm />],
  ["/clientes",                   <ClientesList />],
  ["/contactos",                  <ContactosPage />],
  ["/directorio",                 <DirectorioPage />],
  ["/informacion/cotizador-historico", <CotizadorHistorico />],
];

const operacionesRoutes = [
  ["/operaciones/costo-proyecto",     <CostoProyectoPage />],
  ["/operaciones/costo-ot",           <CostoOTPage />],
  ["/operaciones/activo-circulante",  <ActivoCirculantePage />],
  ["/reportes",                       <Reportes />],
];

const contabilidadRoutes = [
  ["/contabilidad/seguimiento-facturas",    <SeguimientoFacturas />],
  ["/facturacion/proforma",                 <FacturaVenta />],
  ["/facturacion/factura-venta",            <FacturaVenta />],
  ["/facturacion/mantencion",               <MantencionFacturacionPage />],
  ["/contabilidad/flujo-caja",              <FlujoCajaPage />],
  ["/finanzas/conciliacion-bancaria",       <ConciliacionBancaria />],
  ["/contabilidad/cxp-nominas",             <CxPNominasPage />],
  ["/contabilidad/cheques-por-pagar",       <ChequesPorPagarPage />],
  ["/contabilidad/cheques-emitidos",        <ChequesEmitidosPage />],
  // Iter 369 — módulo duplicado de Flujo de Caja retirado del menú; redirige al unificado (Alegra)
  ["/contabilidad/anticipos-proveedor",     <Navigate to="/contabilidad/egresos" replace />],
  ["/contabilidad/anticipos",               <Navigate to="/contabilidad/egresos" replace />],
  ["/contabilidad/cuentas-pagar-alegra",    <CuentasPorPagar />],
  ["/contabilidad/cuentas-pagar-rcv",       <CuentasPorPagarRCV />],
  ["/contabilidad/estado-cuenta-proveedor", <ContabilizarOC />],
  ["/contabilidad/acepta-rechazo-dte",      LazyAceptaRechazoDTE],
  ["/contabilidad/facturas-contabilizar",   <FacturasContabilizar />],
  ["/contabilidad/registro-dte",            <RegistroDTE />],
  ["/contabilidad/rcv-import",              <RCVImportPage />],
  ["/contabilidad/rcv-reportes",            <RCVReportesPage />],
  ["/contabilidad/egresos",                 <EgresosPage />],
  ["/contabilidad/ingresos",                <IngresosPage />],
  ["/contabilidad/regularizacion-anticipos", <RegularizacionAnticiposPage />],
  ["/contabilidad/seguros",                 <SegurosPage />],
  ["/contabilidad/solicitar-estado-cuenta",  <SolicitarEstadosCuenta />],
  ["/contabilidad/duplicados",              <DuplicadosFacturasPage />],
  ["/contabilidad/notas-credito",           <NotasCreditoPage />],
  ["/contabilidad/relinkear-proforma",      <RelinkearProformaPage />],
  ["/cuentas-por-cobrar",                   <CuentasPorCobrar />],
  ["/cuentas-por-pagar",                    <CuentasPorPagar />],
  ["/contabilidad/conciliacion-proveedores", <ConciliacionProveedoresPage />],
  ["/contabilidad/tef-config",               <TefConfigPage />],
  // Iter 416 — Módulo de Emisión de DTE + Cesión/Factoring (Fase 1 MVP)
  ["/emision-dte",                           <EmisionDTEList />],
  ["/emision-dte/:id",                       <EmisionDTEDetail />],
];

const comprasRoutes = [
  ["/compras",                                  <ComprasResumen />],
  ["/compras/ordenes-compra",                   <OrdenesCompraList />],
  ["/compras/auditoria-trazabilidad",           <AuditoriaTrazabilidad />],
  ["/compras/items-oc",                         <ItemsOC />],
  ["/compras/reporte-costo-ot",                 <CostoMaterialesOT />],
  ["/compras/reporte-pendientes",               <ReportePendientes />],
  ["/compras/resumen-ots",                      <ResumenComprasOTs />],
  ["/compras/estado-ot",                        <EstadoComprasOT />],
  ["/compras/generar-oc",                       <GenerarOCPage />],
  ["/compras/editar-oc/:numeroOc",              <GenerarOCPage />],
  ["/compras/requisiciones",                    <Requisiciones />],
  ["/compras/buscador-materiales",              <BuscadorMateriales />],
  ["/compras/proveedores",                      <ProveedoresList />],
  ["/compras/proveedores/auditoria-nombres",    <AuditoriaNombresProveedores />],
  ["/compras/seguimiento",                      <SeguimientoOCPage />],
  ["/compras/trazabilidad",                     <TrazabilidadOT />],
  ["/compras/firma-vb-lote",                    <FirmaVBLote />],
  ["/compras/solicitar-cotizacion",             <SolicitudCotizacion />],
  ["/compras/cotizaciones",                     <CotizacionesList />],
  ["/compras/cotizaciones/:id/comparativo",     <ComparativoCotizacion />],
  // Iter 313.j — Rendiciones de Gastos (Caja Chica / Fondo por Rendir)
  ["/compras/rendiciones-gastos",                <RendicionesGastosList />],
  ["/compras/rendiciones-gastos/nueva",          <RendicionGastosDetail />],
  ["/compras/rendiciones-gastos/:id",            <RendicionGastosDetail />],
  ["/compras/rendiciones-gastos/:id/editar",     <RendicionGastosDetail />],
];

const bodegaRoutes = [
  ["/bodega/recepcion-oc",      <RecepcionOC />],
  ["/bodega/recepcion-rapida",  <RecepcionOCPage />],
  ["/bodega/recepciones",       <RecepcionesListPage />],
  ["/bodega/oc-pendientes-recepcion", <OCPendientesRecepcionPage />],
  ["/finanzas/facturas-pendientes-recepcion", <FacturasPendientesRecepcion />],
  ["/finanzas/pagos-bloqueados",              <PagosBloqueadosPage />],
  ["/finanzas/lotes-servicios",               <LotesServiciosPage />],
  ["/bodega/inventario",        <StockBodega />],
  ["/bodega/maestro-materiales",<MaestroMateriales />],
  ["/bodega/maestro-herramientas",<MaestroHerramientasPage />],
  ["/bodega/panoles",            <PanolesPage />],
  ["/bodega/panoles/bulk-responsables", <PanolesBulkResponsablePage />],
  ["/bodega/panoles/:id",        <PanolDetallePage />],
  ["/bodega/distribucion",       <DistribucionHerramientasPage />],
  ["/bodega/qr-imprimir",        <QRImprimirPage />],
  ["/bodega/inventario-semanal", <InventarioSemanalPage />],
  ["/bodega/transferencias", <TransferenciasPage />],
  ["/bodega/herramientas/timeline", <HerramientaTimelinePage />],
  ["/bodega/herramientas/:codigo/timeline", <HerramientaTimelinePage />],
  // /bodega/transferencias/recibir se registra como ruta PÚBLICA más abajo
  ["/bodega/stock-insumos",     <StockInsumosPage />],
  ["/bodega/precios-historicos",<PreciosHistoricosPage />],
  ["/bodega/despachos",         <DespachosPage />],
  ["/bodega/herramientas",      <HerramientasCargoPage />],
  ["/bodega/inventario-fisico", <InventarioFisicoPage />],
  ["/bodega/dashboard",         <DashboardBodega />],
];

// RRHH: la mayoría requiere TerrenoGuard. Algunas son accesibles a terreno.
const rrhhRoutesWithTerrenoGuard = [
  ["/rrhh",                    <RRHHDashboard />],
  ["/rrhh/trabajadores",       <TrabajadoresPage />],
  ["/rrhh/control-hh",         <ControlHHUnificado />],
  ["/rrhh/resumen-casino",     <ResumenCasino />],
  ["/rrhh/tarjeta-control",    <TarjetaControlPage />],
  ["/rrhh/control-errores",    <ControlErroresPage />],
  ["/rrhh/asistencia-mensual", <AsistenciaMensualPage />],
  ["/rrhh/panel-personal",     <PanelPersonalPage />],
  ["/rrhh/partes-huerfanos",   <HuerfanosPartesPage />],
];

const rrhhRoutesOpenToTerreno = [
  ["/rrhh/parte-diario",       <ParteDiarioTerreno />],
  ["/rrhh/monitor-partes",     <MonitorPartesPage />],
  ["/rrhh/mis-partes",         <MisPartes />],
  ["/rrhh/mis-vb",             <MisVBPage />],
  ["/aprobaciones",            <BandejaAprobaciones />],
  ["/rrhh/resumen-almuerzos",  <ResumenAlmuerzos />],
  ["/rrhh/procedimientos",     <ProcedimientosRRHH />],
  ["/rrhh/vacaciones",         <VacacionesPage />],
];

const configuracionRoutes = [
  ["/configuracion",                  <Configuracion />],
  ["/configuracion/general",          <Configuracion />],
  ["/configuracion/usuarios",         <GestionUsuarios />],
  ["/configuracion/tarjetas-pago",    <TarjetasPagoPage />],
  ["/configuracion/medios-pago",      <MediosPagoPage />],
  ["/configuracion/condiciones-pago", <CondicionesPagoPage />],
  ["/configuracion/monitoreo",        <MonitoreoSistema />],
  ["/configuracion/notificaciones",   <Notificaciones />],
  ["/configuracion/sync-access",      <SyncAccessPanel />],
  ["/configuracion/api-ia",           <ApiIAPage />],
  ["/configuracion/auditoria",        <AuditLogPage />],
  ["/configuracion/feriados-horario", <FeriadosHorarioPage />],
  ["/configuracion/calendario-bancario", <CalendarioBancarioPage />],
  ["/configuracion/recordatorios",    <RecordatoriosPage />],
  ["/configuracion/recordatorios-facturas-recepcion", <RecordatoriosFacRecepPage />],
  ["/configuracion/reportes-programados", <ReportesProgramadosPage />],
  ["/configuracion/reportes-programados/analytics", <ReportesAnalyticsPage />],
  ["/configuracion/recordatorios-inventario", <RecordatoriosInventarioPage />],
  ["/configuracion/migracion-access-htas", <MigracionAccessHtasPage />],
  ["/configuracion/health-check",     <HealthCheckPage />],
  ["/configuracion/cuentas-alegra",   <CuentasAlegraPage />],
];

const utilidadesRoutes = [
  ["/procedimientos",   <Procedimientos />],
  ["/manual",           <ManualUso />],
  ["/admin/calidad",    <CalidadDashboard />],
  ["/admin/code-health", <CodeHealthPage />],
  ["/admin/tarjetas-reporte", <TarjetasReportePage />],
  ["/admin/tarjetas-conciliacion", <TarjetasConciliacionPage />],
  ["/admin/cajas-rendir", <CajasRendirPage />],
  ["/admin/cajas-rendir-reporte", <CajasRendirReportePage />],
  ["/access-database",  <AccessDatabase />],
];

const alegraRoutes = [
  ["/alegra",                       <AlegraIntegration />],
  ["/alegra/rcv-comparator",        <RCVComparator />],
  ["/alegra/proformas-pendientes",  <ProformasPendientes />],
  ["/alegra/recepciones-pendientes",<RecepcionesPendientes />],
  ["/alegra/contabilizar-oc",       <ContabilizarOC />],
];

const reportesRoutes = [
  ["/reportes/gerencia",          <GerenciaReport />],
  ["/reportes/informe-proyecto",  <InformeProyecto />],
  ["/reportes/informe-costo-ot",  <InformeCostoOT />],
];

const dteAliasRoutes = [
  ["/dte/manager", LazyAceptaRechazoDTE],
];

const renderRoutes = (entries, Wrapper = P) =>
  entries.map(([path, el]) => (
    <Route key={path} path={path} element={<Wrapper>{el}</Wrapper>} />
  ));

// ─────────────────────────── Router ───────────────────────────

function AppRoutes() {
  return (
    <Routes>
      {/* Públicas */}
      <Route path="/cotizar/:token" element={<CotizarPublico />} />
      <Route path="/bodega/transferencias/recibir" element={<TransferenciaRecibirPage />} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      {/* Consentimiento OAuth (ChatGPT → ERP): pública, con login propio; no redirige al dashboard */}
      <Route path="/oauth/autorizar" element={<OAuthAutorizar />} />

      {/* Privada raíz */}
      <Route path="/dashboard" element={<P><Dashboard /></P>} />

      {/* Dominios */}
      {renderRoutes(informacionRoutes)}
      {renderRoutes(operacionesRoutes)}
      {renderRoutes(contabilidadRoutes)}
      {renderRoutes(comprasRoutes)}
      {renderRoutes(bodegaRoutes)}
      {renderRoutes(rrhhRoutesWithTerrenoGuard, PT)}
      {renderRoutes(rrhhRoutesOpenToTerreno)}
      {renderRoutes(configuracionRoutes)}
      {renderRoutes(utilidadesRoutes)}
      {renderRoutes(alegraRoutes)}
      {renderRoutes(reportesRoutes)}
      {renderRoutes(dteAliasRoutes)}

      {/* Redirects */}
      <Route path="/"  element={<P><TerrenoRedirect /></P>} />
      <Route path="*"  element={<P><TerrenoRedirect /></P>} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CmdKProvider>
          <DocRefProvider>
            <ErrorBoundary>
              <AppRoutes />
              <Toaster position="top-right" richColors closeButton duration={5000} />
            </ErrorBoundary>
          </DocRefProvider>
        </CmdKProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
